import { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Popper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ts } from 'src/utils/translate';
import { theme } from 'src/theme/mui-theme';

interface RoomDetectedModalProps {
    anchorEl: HTMLElement | null;
    onGotIt: (dontShowAgain: boolean) => void;
}

export function RoomDetectedModal({ anchorEl, onGotIt }: RoomDetectedModalProps) {
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const blue = theme.palette.primary.main;
    const white = theme.palette.common.white;

    return (
        <>
            <Box sx={{ position: 'fixed', inset: 0, backgroundColor: alpha(theme.palette.common.black, 0.3), zIndex: 9998 }} />
            <Popper
                open
                anchorEl={anchorEl}
                placement="right"
                sx={{ zIndex: 9999 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 15] } }]}
            >
                <Box
                    sx={{
                        maxWidth: 450,
                        backgroundColor: blue,
                        p: '20px 25px',
                        borderRadius: 2,
                        position: 'relative',
                        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.3)',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '-15px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 0,
                            height: 0,
                            borderTop: '15px solid transparent',
                            borderBottom: '15px solid transparent',
                            borderRight: `15px solid ${blue}`,
                        },
                    }}
                >
                    <Typography sx={{ color: white, fontWeight: 600, fontSize: 18, mb: 1.25 }}>
                        {ts('room_detected_title', 'Rooms ready for review!')}
                    </Typography>
                    <Typography sx={{ color: white, fontSize: 14, mb: 2.5, lineHeight: 1.5 }}>
                        {ts('room_detected_list_hint', 'You can see the list of all the rooms found here.')}
                        <br />
                        {ts('room_detected_edit_hint', 'Click the ✏️ icon to edit or set the room type and name.')}
                    </Typography>

                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.875}>
                        <FormControlLabel
                            sx={{ color: white, m: 0 }}
                            control={
                                <Checkbox
                                    checked={dontShowAgain}
                                    onChange={(e) => setDontShowAgain(e.target.checked)}
                                    sx={{ color: white, '&.Mui-checked': { color: white }, p: 0.5 }}
                                />
                            }
                            label={
                                <Typography sx={{ color: white, fontSize: 14 }}>
                                    {ts('do_not_show_again', "Don't show again")}
                                </Typography>
                            }
                        />
                        <Button
                            onClick={() => onGotIt(dontShowAgain)}
                            sx={{
                                backgroundColor: alpha(theme.palette.common.black, 0.2),
                                color: white,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: alpha(theme.palette.common.black, 0.3) },
                            }}
                        >
                            {ts('room_detected_got_it', 'Got It!')}
                        </Button>
                    </Stack>
                </Box>
            </Popper>
        </>
    );
}
